import React, { useState } from 'react';
import { SPACE, RADII, FONT_SIZE, FONTS } from '../../../theme';
import { KeyBinding } from '../../../../../shared/CharacterTypes';
import { ActionBindingRow } from '../keybindingGroups';
import { bindingId } from '../keybindingKeymap';

export interface KeybindingTableProps {
  rows: ActionBindingRow[];
  capturingTarget: { action: string; slot: 'primary' | 'secondary' } | null;
  onBeginCapture: (action: string, slot: 'primary' | 'secondary', binding: KeyBinding | null) => void;
  onClearSlot: (action: string, slot: 'primary' | 'secondary', binding: KeyBinding) => void;
  onResetAction?: (action: string) => void;
  onHoverAction?: (action: string | null) => void;
  highlightedAction?: string | null;
  captureError?: string | null;
  searchActive?: boolean;
  /** When true (e.g. a single Action Bar is selected in the left rail), bar rows render directly without a fold toggle. */
  forceExpandBars?: boolean;
}

export const KeybindingTable: React.FC<KeybindingTableProps> = ({
  rows,
  capturingTarget,
  onBeginCapture,
  onClearSlot,
  onResetAction,
  onHoverAction,
  highlightedAction,
  captureError,
  searchActive = false,
  forceExpandBars = false,
}) => {
  // Bar 1 is open by default; extra bars (Bar 2..6) are folded by default.
  const [openBars, setOpenBars] = useState<Record<number, boolean>>({ 1: true });
  const toggleBarSection = (barId: number) => {
    setOpenBars((prev) => ({ ...prev, [barId]: !(prev[barId] ?? barId === 1) }));
  };
  const isCapturing = (action: string, slot: 'primary' | 'secondary') =>
    capturingTarget?.action === action && capturingTarget?.slot === slot;

  const renderKeyChip = (
    action: string,
    slot: 'primary' | 'secondary',
    binding: KeyBinding | null,
    displayName: string
  ) => {
    const capturing = isCapturing(action, slot);
    const id = binding ? bindingId(binding) : `unbound-${action}-${slot}`;
    const label = binding ? `${binding.modifier ? `${binding.modifier.toUpperCase()}+` : ''}${binding.key}` : '+ Unbound';

    return (
      <div style={chipWrapperStyle}>
        <button
          type="button"
          data-testid={binding ? `keybind-${id}` : `keybind-${action}-${slot}`}
          aria-label={`${capturing ? 'Capture' : 'Change'} ${displayName} ${slot} key, current ${label}`}
          onClick={() => onBeginCapture(action, slot, binding)}
          style={capturing ? chipCapturingStyle : binding ? chipBoundStyle : chipUnboundStyle}
          title={capturing ? 'Press a key… (Esc to cancel)' : binding ? 'Click to change' : 'Click to bind key'}
        >
          {capturing ? 'Press a key…' : label}
        </button>
        {binding && !capturing && (
          <button
            type="button"
            data-testid={`clear-${action}-${slot}`}
            aria-label={`Clear ${slot} binding for ${displayName}`}
            onClick={(e) => {
              e.stopPropagation();
              onClearSlot(action, slot, binding);
            }}
            style={clearBtnStyle}
            title="Unbind this key"
          >
            ×
          </button>
        )}
      </div>
    );
  };

  return (
    <div style={tableContainerStyle} role="region" aria-label="Keybinding Table">
      {/* Header Row */}
      <div style={headerRowStyle}>
        <div style={colHeaderActionStyle}>Action</div>
        <div style={colHeaderSlotStyle}>Primary</div>
        <div style={colHeaderSlotStyle}>Secondary</div>
        <div style={colHeaderResetStyle}>Reset</div>
      </div>

      {/* Rows */}
      <div style={rowsListStyle}>
        {(() => {
          const renderedBarIds = new Set<number>();
          const renderRow = (row: ActionBindingRow) => {
            const isRowCapturing = capturingTarget?.action === row.action;
            const isHighlighted = highlightedAction === row.action;

            return (
              <div
                key={row.action}
                onMouseEnter={() => onHoverAction?.(row.action)}
                onMouseLeave={() => onHoverAction?.(null)}
                style={{
                  ...rowStyle,
                  ...(isHighlighted ? rowHighlightedStyle : {}),
                  ...(isRowCapturing ? rowActiveStyle : {}),
                }}
              >
                {/* Action Name & Description */}
                <div style={actionInfoColStyle}>
                  <div style={actionNameStyle}>{row.displayName}</div>
                  {row.description && <div style={actionDescStyle}>{row.description}</div>}
                </div>

                {/* Primary Slot */}
                <div style={slotColStyle}>
                  {renderKeyChip(row.action, 'primary', row.primary, row.displayName)}
                </div>

                {/* Secondary Slot */}
                <div style={slotColStyle}>
                  {renderKeyChip(row.action, 'secondary', row.secondary, row.displayName)}
                  {row.extraBindings.length > 0 && (
                    <span
                      style={extraBadgeStyle}
                      title={`Additional: ${row.extraBindings.map((b) => b.key).join(', ')}`}
                    >
                      +{row.extraBindings.length}
                    </span>
                  )}
                </div>

                {/* Reset Action Column */}
                <div style={resetColStyle}>
                  {onResetAction && (
                    <button
                      type="button"
                      data-testid={`reset-action-${row.action}`}
                      aria-label={`Reset ${row.displayName} to default`}
                      onClick={() => onResetAction(row.action)}
                      style={resetActionBtnStyle}
                      title="Reset this action to default"
                    >
                      ↺
                    </button>
                  )}
                </div>
              </div>
            );
          };

          return rows.map((row) => {
            if (row.barId === undefined) {
              return renderRow(row);
            }
            const barId = row.barId;
            if (renderedBarIds.has(barId)) return null;
            renderedBarIds.add(barId);

            const barRows = rows.filter((r) => r.barId === barId);
            const isOpen = forceExpandBars || searchActive || (openBars[barId] ?? barId === 1);
            const boundCount = barRows.filter((r) => r.primary !== null || r.secondary !== null).length;

            return (
              <div key={`bar-section-${barId}`} data-testid={`controls-bar-group-${barId}`}>
                <button
                  type="button"
                  data-testid={`controls-bar-section-${barId}`}
                  aria-expanded={isOpen}
                  onClick={() => toggleBarSection(barId)}
                  style={barSectionHeaderStyle}
                >
                  <span>
                    {isOpen ? '▾' : '▸'} Bar {barId}{barId === 1 ? ' (Main Bar)' : ''}
                  </span>
                  <span style={barSectionCountStyle}>
                    {boundCount} / {barRows.length} bound
                  </span>
                </button>
                {isOpen && barRows.map(renderRow)}
              </div>
            );
          });
        })()}
      </div>

      {captureError && (
        <div
          data-testid="keybind-error"
          role="alert"
          style={errorAlertStyle}
        >
          {captureError}
        </div>
      )}
    </div>
  );
};

const tableContainerStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
  height: '100%',
  minHeight: 0,
  background: 'linear-gradient(180deg, rgba(250, 243, 224, 0.78), rgba(238, 226, 198, 0.78))',
  borderRadius: RADII.md,
  border: '1px solid var(--tome-rule-strong, rgba(92, 64, 28, 0.38))',
  boxShadow: 'inset 0 1px 0 rgba(255, 255, 255, 0.65), 0 1px 3px rgba(70, 48, 14, 0.1)',
  overflow: 'hidden',
};

const headerRowStyle: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'minmax(130px, 1.55fr) minmax(92px, 1fr) minmax(92px, 1fr) 40px',
  alignItems: 'center',
  padding: `${SPACE.sm}px ${SPACE.sm}px`,
  background: 'linear-gradient(180deg, rgba(232, 216, 182, 0.88), rgba(218, 198, 158, 0.88))',
  borderBottom: '1px solid var(--tome-rule-strong, rgba(92, 64, 28, 0.38))',
  fontSize: FONT_SIZE.xs,
  fontFamily: FONTS.ui,
  color: 'var(--tome-ochre, #8f560f)',
  textTransform: 'uppercase',
  letterSpacing: '0.08em',
};

const colHeaderActionStyle: React.CSSProperties = {
  fontWeight: 'bold',
};

const colHeaderSlotStyle: React.CSSProperties = {
  textAlign: 'center',
  fontWeight: 'bold',
};

const colHeaderResetStyle: React.CSSProperties = {
  textAlign: 'center',
  fontWeight: 'bold',
};

const rowsListStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  flex: '1 1 auto',
  minHeight: 0,
  maxHeight: 460,
  overflowY: 'auto',
};

const rowStyle: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'minmax(130px, 1.55fr) minmax(92px, 1fr) minmax(92px, 1fr) 40px',
  alignItems: 'center',
  padding: `${SPACE.xs + 2}px ${SPACE.sm}px`,
  borderBottom: '1px solid var(--tome-rule, rgba(92, 64, 28, 0.18))',
  transition: 'background 0.15s ease',
};

const rowHighlightedStyle: React.CSSProperties = {
  background: 'rgba(229, 192, 105, 0.28)',
};

const rowActiveStyle: React.CSSProperties = {
  background: 'rgba(229, 192, 105, 0.38)',
};

const actionInfoColStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  paddingRight: SPACE.sm,
};

const actionNameStyle: React.CSSProperties = {
  fontSize: FONT_SIZE.sm,
  fontFamily: FONTS.ui,
  color: 'var(--tome-ink, #24180c)',
  fontWeight: 700,
};

const actionDescStyle: React.CSSProperties = {
  fontSize: 10,
  fontFamily: FONTS.ui,
  color: 'var(--tome-ink-muted, #6b5a43)',
  marginTop: 1,
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
};

const slotColStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 4,
};

const chipWrapperStyle: React.CSSProperties = {
  position: 'relative',
  display: 'inline-flex',
  alignItems: 'center',
  width: '100%',
  maxWidth: 130,
};

const chipBoundStyle: React.CSSProperties = {
  width: '100%',
  background: 'linear-gradient(180deg, #fcf7e8 0%, #eadac0 100%)',
  padding: '5px 8px',
  borderRadius: RADII.sm,
  border: '1px solid var(--tome-gilt, #b98a2e)',
  boxShadow: 'inset 0 1px 0 rgba(255, 255, 255, 0.8), 0 1px 2px rgba(60, 40, 10, 0.14)',
  color: 'var(--tome-ink, #24180c)',
  fontFamily: FONTS.mono,
  fontSize: 11,
  fontWeight: 'bold',
  textAlign: 'center',
  cursor: 'pointer',
  transition: 'border-color 0.15s ease, background 0.15s ease',
  boxSizing: 'border-box',
};

const chipUnboundStyle: React.CSSProperties = {
  ...chipBoundStyle,
  background: 'rgba(255, 252, 240, 0.38)',
  border: '1px dashed var(--tome-rule-strong, rgba(92, 64, 28, 0.38))',
  boxShadow: 'none',
  color: 'var(--tome-ink-muted, #6b5a43)',
  fontWeight: 'normal',
};

const chipCapturingStyle: React.CSSProperties = {
  ...chipBoundStyle,
  background: 'linear-gradient(180deg, #f6dc98 0%, #e2b85c 100%)',
  border: '1px solid var(--tome-ochre, #8f560f)',
  color: '#2a1a06',
  animation: 'pulse 1s infinite alternate',
};

const clearBtnStyle: React.CSSProperties = {
  position: 'absolute',
  right: 4,
  background: 'transparent',
  border: 'none',
  color: 'var(--tome-ink-muted, #6b5a43)',
  fontSize: 12,
  cursor: 'pointer',
  padding: '0 2px',
  lineHeight: 1,
};

const extraBadgeStyle: React.CSSProperties = {
  fontSize: 9,
  padding: '2px 4px',
  borderRadius: 3,
  background: 'rgba(92, 64, 28, 0.14)',
  color: 'var(--tome-ink-soft, #3e3021)',
  cursor: 'help',
};

const resetColStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
};

const resetActionBtnStyle: React.CSSProperties = {
  background: 'transparent',
  border: 'none',
  color: 'var(--tome-ink-muted, #6b5a43)',
  fontSize: 14,
  cursor: 'pointer',
  padding: 4,
  lineHeight: 1,
  borderRadius: 4,
  transition: 'color 0.15s ease',
};

const errorAlertStyle: React.CSSProperties = {
  padding: `${SPACE.sm}px ${SPACE.md}px`,
  fontSize: FONT_SIZE.xs,
  color: 'var(--tome-danger, #9b1f17)',
  fontWeight: 600,
  background: 'rgba(155, 31, 23, 0.12)',
  borderTop: '1px solid rgba(155, 31, 23, 0.35)',
  textAlign: 'center',
};

const barSectionHeaderStyle: React.CSSProperties = {
  width: '100%',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: `${SPACE.xs + 2}px ${SPACE.md}px`,
  background: 'linear-gradient(90deg, rgba(229, 192, 105, 0.34), rgba(255, 252, 240, 0.45))',
  border: 'none',
  borderBottom: '1px solid var(--tome-rule-strong, rgba(92, 64, 28, 0.38))',
  color: 'var(--tome-ochre, #8f560f)',
  fontFamily: FONTS.display,
  fontSize: FONT_SIZE.xs,
  fontWeight: 'bold',
  letterSpacing: '0.04em',
  cursor: 'pointer',
  textAlign: 'left',
};

const barSectionCountStyle: React.CSSProperties = {
  fontFamily: FONTS.ui,
  fontSize: 10,
  fontWeight: 'normal',
  color: 'var(--tome-ink-muted, #6b5a43)',
};
